/**
 * geminiAudioBridge — the ONLY place that touches native audio for the Gemini
 * live-translation path. Everything react-native-audio-api-specific lives here
 * so useGeminiLiveTranslationSession stays pure protocol/state, and so the one
 * binding that can only be validated on a real EAS build is a single, clearly
 * marked file.
 *
 * Responsibilities:
 *   - Capture mic as raw PCM16 @ 16 kHz (Gemini Live input format) and hand
 *     each ~100 ms chunk back as base64.
 *   - Play streamed PCM16 @ 24 kHz (Gemini Live output format) gaplessly by
 *     scheduling each chunk on a running playhead.
 *   - HALF-DUPLEX echo control: the WebRTC path got hardware AEC for free; the
 *     raw-PCM path does not, so while translated audio is playing (plus a
 *     short hangover) we DROP mic chunks. Without this, the speaker's output
 *     is re-captured, fed to the other-direction session, and re-translated —
 *     a feedback loop. iOS `voiceChat` mode below also enables the system
 *     voice-processing AEC as a second line of defence.
 *
 * ⚠️ react-native-audio-api is a native module — this only runs in a custom
 * dev/prod build (EAS), never Expo Go. The exact AudioRecorder option names
 * (bufferLengthInSamples) and AudioManager option shape are the bits most
 * likely to need a tweak on first device run; they're called out inline.
 */
import { GEMINI_LIVE_AUDIO_INPUT_RATE, GEMINI_LIVE_AUDIO_OUTPUT_RATE } from "@taskhealth/shared";
import * as AudioApiRaw from "react-native-audio-api";

// ─── Minimal facade over react-native-audio-api ────────────
// We depend on OUR interface, not the library's generated types, so this file
// compiles as long as the module resolves (the surface we use is small and
// stable). Cast once, here.
interface RNAudioBuffer {
    getChannelData(channel: number): Float32Array;
}
interface RNAudioBufferSource {
    buffer: RNAudioBuffer | null;
    connect(destination: unknown): void;
    start(when?: number): void;
    stop(when?: number): void;
    onended: (() => void) | null;
}
interface RNAudioContext {
    readonly currentTime: number;
    readonly state: string;
    readonly destination: unknown;
    createBuffer(channels: number, length: number, sampleRate: number): RNAudioBuffer;
    createBufferSource(): RNAudioBufferSource;
    resume(): Promise<void>;
    close(): Promise<void> | void;
}
interface RNAudioRecorder {
    onAudioReady(
        options: { sampleRate: number; bufferLength: number; channelCount: number },
        callback: (event: { buffer: RNAudioBuffer; numFrames: number; when: number }) => void
    ): void;
    start(): void;
    stop(): void;
}
interface RNAudioManager {
    setAudioSessionOptions(opts: {
        iosCategory?: string;
        iosMode?: string;
        iosOptions?: string[];
    }): void;
    setAudioSessionActivity(enabled: boolean): Promise<boolean>;
    requestRecordingPermissions(): Promise<string>;
}
interface RNAudioApiModule {
    AudioContext: new (opts?: { sampleRate?: number }) => RNAudioContext;
    AudioRecorder: new () => RNAudioRecorder;
    AudioManager: RNAudioManager;
}

const AudioApi = AudioApiRaw as unknown as RNAudioApiModule;

// ~100 ms of 16 kHz mono — the chunk size Gemini's docs recommend for low
// latency. Smaller = lower latency but more WS frames; larger = the reverse.
const MIC_BUFFER_SAMPLES = Math.round(GEMINI_LIVE_AUDIO_INPUT_RATE * 0.1);
// Keep the mic gated for this long after the last scheduled playback ends, so
// the speaker's tail doesn't leak into the mic and re-translate.
const ECHO_HANGOVER_SEC = 0.4;
// Small scheduling lead so the first chunk of a turn doesn't start in the past.
const PLAYBACK_LEAD_SEC = 0.08;

export interface GeminiAudioBridge {
    /** Acquire mic + audio session, begin emitting base64 PCM16/16k chunks.
     *  Chunks captured while playback is active are dropped (half-duplex). */
    start(onMicChunk: (base64Pcm16: string) => void): Promise<void>;
    /** Queue a base64 PCM16/24k chunk for gapless playback on the speaker. */
    enqueuePlayback(base64Pcm16: string): void;
    /** True while translated audio is scheduled/playing (drives the pill UI). */
    isPlaying(): boolean;
    /** Stop capture + playback and release the audio session. */
    stop(): Promise<void>;
}

export function createGeminiAudioBridge(): GeminiAudioBridge {
    let recorder: RNAudioRecorder | null = null;
    let playbackCtx: RNAudioContext | null = null;
    // Absolute ctx.currentTime up to which audio is already scheduled.
    let scheduledUntil = 0;
    // Wall-clock (ms) until which translated audio is expected to be playing.
    // The echo gate keys off THIS, not ctx.currentTime — on react-native-audio-api
    // the context can report a non-advancing currentTime, which would otherwise
    // pin the gate closed and mute the mic forever.
    let playbackUntilMs = 0;
    let stopped = false;
    // Keep scheduled playback sources referenced until they finish, so the JS
    // wrapper (and its native node) isn't garbage-collected before its scheduled
    // start time — which would silently drop the chunk.
    const activeSources = new Set<RNAudioBufferSource>();

    function ensureContext(): RNAudioContext {
        if (!playbackCtx) {
            // Pin the context to Gemini's 24 kHz output rate. The native engine
            // plays our 24 kHz buffers sample-for-sample at the context rate (it
            // does NOT resample by the buffer's declared rate), so a default
            // 48 kHz context played them ~2x too fast → garbled, high-pitched.
            // Matching the rate fixes the pitch; CoreAudio handles the final
            // 24 kHz → hardware-rate conversion at the output.
            playbackCtx = new AudioApi.AudioContext({
                sampleRate: GEMINI_LIVE_AUDIO_OUTPUT_RATE
            });
            scheduledUntil = playbackCtx.currentTime;
        }
        return playbackCtx;
    }

    function micGated(): boolean {
        // Gate the mic only while translated audio is actually playing (+ a
        // short hangover), measured in WALL-CLOCK time so a non-advancing
        // ctx.currentTime can't pin the gate shut and mute capture forever.
        return Date.now() < playbackUntilMs + ECHO_HANGOVER_SEC * 1000;
    }

    return {
        async start(onMicChunk) {
            stopped = false;

            // playAndRecord + defaultToSpeaker: simultaneous mic + loudspeaker.
            // iosMode "default" (NOT voiceChat) — voiceChat's voice-processing I/O
            // unit routes call-style audio to the earpiece and can swallow
            // AudioContext playback; echo is handled by the half-duplex mic gate.
            // allowBluetoothHFP keeps a paired headset's mic+speaker usable.
            try {
                AudioApi.AudioManager.setAudioSessionOptions({
                    iosCategory: "playAndRecord",
                    iosMode: "default",
                    iosOptions: ["defaultToSpeaker", "allowBluetoothHFP"]
                });
                await AudioApi.AudioManager.setAudioSessionActivity(true);
                await AudioApi.AudioManager.requestRecordingPermissions();
            } catch (e) {
                console.warn("[gemini-translation] audio session setup failed", e);
            }

            const ctx = ensureContext();
            // The context is created SUSPENDED (currentTime stays 0), so scheduled
            // playback never advances and is silent. Resume once so the clock runs.
            // Exactly one awaited call — a double/concurrent resume() crashed the
            // native audio engine.
            try {
                await ctx.resume();
            } catch (e) {
                console.warn("[gemini-audio] ctx resume failed", e);
            }

            const rec = new AudioApi.AudioRecorder();
            recorder = rec;
            // onAudioReady takes (options, callback); the callback runs on the
            // JS thread (not a worklet), so calling back into onMicChunk → WS
            // send is fine.
            rec.onAudioReady(
                {
                    sampleRate: GEMINI_LIVE_AUDIO_INPUT_RATE,
                    bufferLength: MIC_BUFFER_SAMPLES,
                    channelCount: 1
                },
                ({ buffer, numFrames }) => {
                    if (stopped || micGated()) {
                        return;
                    }
                    const samples = buffer.getChannelData(0);
                    onMicChunk(float32ToPcm16Base64(samples, numFrames));
                }
            );
            rec.start();
        },

        enqueuePlayback(base64Pcm16) {
            if (stopped) {
                return;
            }
            const ctx = ensureContext();
            const float = pcm16Base64ToFloat32(base64Pcm16);
            if (float.length === 0) {
                return;
            }
            // Skip near-silent frames. The model streams continuous audio (often
            // silence between translations); queuing it would hold the half-duplex
            // mic gate closed and stall translation after the first reply, plus add
            // choppy filler. Only real audio plays and extends the gate.
            let peak = 0;
            for (let i = 0; i < float.length; i++) {
                const a = float[i] < 0 ? -float[i] : float[i];
                if (a > peak) {
                    peak = a;
                }
            }
            if (peak < 0.01) {
                return;
            }
            const audioBuffer = ctx.createBuffer(1, float.length, GEMINI_LIVE_AUDIO_OUTPUT_RATE);
            audioBuffer.getChannelData(0).set(float);

            const source = ctx.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(ctx.destination);
            // Retain until playback finishes (prevents premature GC → dropped audio).
            activeSources.add(source);
            source.onended = () => {
                activeSources.delete(source);
            };

            const startAt = Math.max(ctx.currentTime + PLAYBACK_LEAD_SEC, scheduledUntil);
            const durationSec = float.length / GEMINI_LIVE_AUDIO_OUTPUT_RATE;
            source.start(startAt);
            scheduledUntil = startAt + durationSec;
            // Mirror the schedule in wall-clock for the echo gate / isPlaying.
            playbackUntilMs = Math.max(Date.now(), playbackUntilMs) + durationSec * 1000;
        },

        isPlaying() {
            return Date.now() < playbackUntilMs;
        },

        async stop() {
            stopped = true;
            if (recorder) {
                try {
                    recorder.stop();
                } catch {
                    // ignore
                }
                recorder = null;
            }
            if (playbackCtx) {
                try {
                    await playbackCtx.close();
                } catch {
                    // ignore
                }
                playbackCtx = null;
            }
            scheduledUntil = 0;
            playbackUntilMs = 0;
            activeSources.clear();
        }
    };
}

// ─── PCM <-> base64 (no external deps) ─────────────────────

const B64_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const B64_LOOKUP: number[] = (() => {
    const t = new Array<number>(256).fill(-1);
    for (let i = 0; i < B64_CHARS.length; i++) {
        t[B64_CHARS.charCodeAt(i)] = i;
    }
    return t;
})();

/** Float32 [-1,1] → little-endian PCM16 → base64. `frames` may be < the
 *  buffer length when the recorder hands a partially-filled buffer. */
function float32ToPcm16Base64(samples: Float32Array, frames: number): string {
    const n = Math.min(frames, samples.length);
    const bytes = new Uint8Array(n * 2);
    for (let i = 0; i < n; i++) {
        let v = samples[i];
        v = v < -1 ? -1 : v > 1 ? 1 : v;
        const s = v < 0 ? v * 0x8000 : v * 0x7fff;
        const int = s | 0;
        bytes[i * 2] = int & 0xff;
        bytes[i * 2 + 1] = (int >> 8) & 0xff;
    }
    return bytesToBase64(bytes);
}

/** base64 → little-endian PCM16 → Float32 [-1,1]. */
function pcm16Base64ToFloat32(b64: string): Float32Array {
    const bytes = base64ToBytes(b64);
    const n = bytes.length >> 1;
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const lo = bytes[i * 2];
        const hi = bytes[i * 2 + 1];
        let int = (hi << 8) | lo;
        if (int >= 0x8000) {
            int -= 0x10000;
        }
        out[i] = int < 0 ? int / 0x8000 : int / 0x7fff;
    }
    return out;
}

function bytesToBase64(bytes: Uint8Array): string {
    let out = "";
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out +=
            B64_CHARS[(n >> 18) & 63] +
            B64_CHARS[(n >> 12) & 63] +
            B64_CHARS[(n >> 6) & 63] +
            B64_CHARS[n & 63];
    }
    const rem = bytes.length - i;
    if (rem === 1) {
        const n = bytes[i] << 16;
        out += B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] + "==";
    } else if (rem === 2) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
        out +=
            B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] + B64_CHARS[(n >> 6) & 63] + "=";
    }
    return out;
}

function base64ToBytes(b64: string): Uint8Array {
    let len = b64.length;
    while (len > 0 && (b64[len - 1] === "=" || b64[len - 1] === "\n" || b64[len - 1] === "\r")) {
        len--;
    }
    const outLen = (len * 3) >> 2;
    const bytes = new Uint8Array(outLen);
    let p = 0;
    let acc = 0;
    let accBits = 0;
    for (let i = 0; i < len; i++) {
        const c = B64_LOOKUP[b64.charCodeAt(i)];
        if (c < 0) {
            continue;
        }
        acc = (acc << 6) | c;
        accBits += 6;
        if (accBits >= 8) {
            accBits -= 8;
            bytes[p++] = (acc >> accBits) & 0xff;
        }
    }
    return p === bytes.length ? bytes : bytes.subarray(0, p);
}
